import {useDeferredValue, useEffect, useRef, useState} from "react";
import {Alert, Form, ListGroup, Spinner} from "react-bootstrap";
import PropTypes from "prop-types";
import {
    addressFromPlace, hasGooglePlacesKey, loadGooglePlaces, REGION_BOUNDS
} from "../../lib/googlePlaces";

function suggestionLabel(suggestion) {
    const prediction = suggestion.placePrediction;
    return prediction?.text?.toString?.()
        || prediction?.text?.text
        || prediction?.mainText?.toString?.()
        || 'Address suggestion';
}

export default function AddressAutocompleteField({label, value, onChange, region, required = false}) {
    const [input, setInput] = useState(value?.formatted_address || '');
    const deferredInput = useDeferredValue(input);
    const [suggestions, setSuggestions] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const sessionToken = useRef(null);

    useEffect(() => {
        setInput(value?.formatted_address || '');
    }, [value?.formatted_address]);

    useEffect(() => {
        if (!hasGooglePlacesKey() || deferredInput.trim().length < 3
            || (value?.google_place_id && deferredInput === value?.formatted_address)) {
            setSuggestions([]);
            return undefined;
        }
        let active = true;
        const timer = window.setTimeout(async () => {
            setLoading(true);
            setError(null);
            try {
                const places = await loadGooglePlaces();
                if (!sessionToken.current) sessionToken.current = new places.AutocompleteSessionToken();
                const request = {
                    input: deferredInput,
                    includedRegionCodes: ['au'],
                    language: 'en-AU',
                    region: 'au',
                    sessionToken: sessionToken.current,
                    ...(REGION_BOUNDS[region] ? {locationBias: REGION_BOUNDS[region]} : {}),
                };
                const response = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
                if (active) setSuggestions(response.suggestions || []);
            } catch (loadError) {
                if (active) {
                    setError(loadError.message || 'Address suggestions are unavailable');
                    setSuggestions([]);
                }
            } finally {
                if (active) setLoading(false);
            }
        }, 250);
        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [deferredInput, region, value?.formatted_address, value?.google_place_id]);

    async function selectSuggestion(suggestion) {
        setLoading(true);
        setError(null);
        try {
            const place = suggestion.placePrediction.toPlace();
            await place.fetchFields({fields: ['id', 'formattedAddress', 'addressComponents', 'location']});
            const address = addressFromPlace(place);
            if (address.country_code !== 'AU') throw new Error('Select an Australian address');
            onChange(address);
            setInput(address.formatted_address);
            setSuggestions([]);
            sessionToken.current = null;
        } catch (selectionError) {
            setError(selectionError.message || 'Unable to select this address');
        } finally {
            setLoading(false);
        }
    }

    function changeInput(event) {
        const next = event.target.value;
        setInput(next);
        if (!next) {
            onChange(null);
            setError(null);
        } else if (next !== value?.formatted_address || !value?.google_place_id) {
            onChange({formatted_address: next, country_code: 'AU'});
            setError('Select an address from the suggestions so it can be saved.');
        }
    }

    return (
        <Form.Group className="mb-3">
            <Form.Label>{label}</Form.Label>
            <div className="position-relative">
                <Form.Control
                    type="search"
                    autoComplete="off"
                    value={input}
                    onChange={changeInput}
                    placeholder="Start typing an Australian address"
                    required={required}
                    readOnly={!hasGooglePlacesKey()}
                    isInvalid={Boolean(input && !value?.google_place_id)}
                    aria-describedby={error ? `${label.replaceAll(' ', '-')}-error` : undefined}
                />
                {loading ? <Spinner animation="border" size="sm" className="worker-address-spinner" /> : null}
                {suggestions.length > 0 ? (
                    <ListGroup className="worker-address-suggestions shadow-sm">
                        {suggestions.map((suggestion, index) => (
                            <ListGroup.Item
                                key={`${suggestion.placePrediction?.placeId || index}`}
                                action
                                type="button"
                                onMouseDown={event => event.preventDefault()}
                                onClick={() => selectSuggestion(suggestion)}
                            >
                                {suggestionLabel(suggestion)}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                ) : null}
            </div>
            {!hasGooglePlacesKey() ? (
                <Form.Text className="text-warning">Address editing requires REACT_APP_GOOGLE_MAPS_API_KEY.</Form.Text>
            ) : null}
            {error ? <Alert id={`${label.replaceAll(' ', '-')}-error`} variant="warning" className="py-1 mt-2 mb-0">{error}</Alert> : null}
        </Form.Group>
    );
}

AddressAutocompleteField.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    region: PropTypes.string,
    required: PropTypes.bool,
};
